import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import createSharedNameString from "Payments/ManagePaymentMethods/createSharedNameString";
import { AnimatePresence, motion } from "framer-motion";

import FnrEnrichedCheckbox, {
  FnrCheckboxIcon,
  FnrCheckboxSubtext,
  FnrCheckboxText,
} from "UI/FnrEnrichedCheckbox";
import FnrHoverButton from "UI/FnrHoverButton";
import FnrInput from "UI/FnrInput";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";
import useToggle from "Utilities/Hooks/useToggle";

import { usePaymentMethodState } from "./";
import DeleteConfirmationPopover from "./DeleteConfirmationPopover";

export default function AdditionalCardSettings({
  hasMobilePhone,
  sharableWith,
  networkName,
  leadText,
  currentScreen,
  verbiage,
}) {
  const {
    paymentType,
    nickname,
    isSavedPaymentMethod,
    isDefaultPaymentMethod,
    isTextToGiveEnabled,
    isEnabledForTTG = true,
    isSharedWithHousehold,
    sharedWithOtherHoH,
    hasOtherPaymentMethods,
    mustSaveCard,
    isAlreadyExisting,
    setState,
    toggleState,
    submitting,
    deleting,
  } = usePaymentMethodState();

  const isBank = paymentType === "bank";

  const noun = verbiage?.noun || "gift";

  // const isSharedWithHousehold = sharedWithOtherHoH;

  // Per OB, we are removing the TTG setting option for now so we are always treating it as if they do not have a mobile phone
  hasMobilePhone = false;

  let isPaymentForm = currentScreen === "paymentForm";

  const isMounted = useComponentIsMounted();

  const [showNickname, toggleShowNickname] = useToggle(
    nickname?.length > 0 ||
    (!hasOtherPaymentMethods && !hasMobilePhone && sharableWith?.length === 0)
  );

  // useEffect(() => {
  //   setState({ showingNicknameCheckbox: showNickname });
  // }, [showNickname, setState]);

  const setNickname = (newValue) => setState({ nickname: newValue });
  const toggleIsSavedPaymentMethod = () => toggleState("isSavedPaymentMethod");
  const toggleIsDefaultPaymentMethod = () => {
    if (isDefaultPaymentMethod && isTextToGiveEnabled && !isAlreadyExisting) {
      // uncheck TTG if they manually choose to not set this card as their default
      toggleState("isTextToGiveEnabled");
    }
    toggleState("isDefaultPaymentMethod");
  };
  const toggleIsTextToGiveEnabled = () => toggleState("isTextToGiveEnabled");
  const toggleIsSharedWithHousehold = () =>
    toggleState("isSharedWithHousehold");

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [updatedSharedWithHoH, setUpdatedSharedWithHoH] = useState(false);
  const [updatedEnabledTTG, setUpdatedEnabledTTG] = useState(false);
  const deleteButtonRef = useRef();

  const [defaultHasBeenEdited, setDefaultHasBeenEdited] = useState();

  const closeDeleteConfirmation = () => {
    if (!deleting) setShowDeleteConfirmation(false);
  };

  const nicknameInputRef = useRef();

  useEffect(() => {
    if (!updatedSharedWithHoH) {
      setState({
        isSharedWithHousehold:
          sharedWithOtherHoH === undefined ? true : sharedWithOtherHoH,
      });
      setUpdatedSharedWithHoH(true);
    }
  }, [updatedSharedWithHoH, sharedWithOtherHoH, setState]);

  useEffect(() => {
    if (!updatedEnabledTTG) {
      setState({ isTextToGiveEnabled: isEnabledForTTG });
      setUpdatedEnabledTTG(true);
    }
  }, [updatedEnabledTTG, isEnabledForTTG, setState]);

  const shareWithNames = createSharedNameString(sharableWith);

  const nickNameAnimateInComplete = () => {
    setTimeout(() => {
      if (nicknameInputRef.current) nicknameInputRef.current.focus();
    }, 10);
  };

  const handleNicknameToggle = (e) => {
    toggleShowNickname(e);
    setState({ showingNicknameCheckbox: !showNickname });
  };

  const shouldShowDefaultCheckbox = () => {
    if (isAlreadyExisting) {
      return (
        defaultHasBeenEdited ||
        (hasOtherPaymentMethods && !isDefaultPaymentMethod)
      );
    } else {
      return hasOtherPaymentMethods;
    }
  };

  const handleDefaultOnToggle = (toggleData) => {
    if (!defaultHasBeenEdited) setDefaultHasBeenEdited(true);
    toggleIsDefaultPaymentMethod(toggleData);
  };

  const displaySavePaymentOptionsInside = () => {
    return (
      hasMobilePhone ||
      sharableWith?.length > 0 ||
      shouldShowDefaultCheckbox() ||
      !isPaymentForm ||
      isAlreadyExisting
    );
  };

  return (
    <Wrapper>
      {!mustSaveCard && !isAlreadyExisting && (
        <FnrEnrichedCheckbox
          on={isSavedPaymentMethod}
          toggleOn={toggleIsSavedPaymentMethod}
          style={{
            paddingTop: 0,
          }}
          wrapperStyle={{ margin: "0 12px" }}
          disabled={submitting || deleting}
        >
          <FnrCheckboxIcon className="fas fa-lock" />
          <FnrCheckboxText>
            Save this {isBank ? "account" : "card"}
          </FnrCheckboxText>
          <FnrCheckboxSubtext>
            We can save this {isBank ? "account" : "card"} for you to use next
            time you make a {noun} to {networkName}.
          </FnrCheckboxSubtext>
        </FnrEnrichedCheckbox>
      )}
      <AnimatePresence>
        {isSavedPaymentMethod && (
          <SavedPaymentOptions
            initial={
              isMounted && {
                height: 0,
                opacity: 0,
                scale: 0.9,
              }
            }
            animate={{
              opacity: 1,
              scale: 1,
              height: "auto",
              transition: { delay: 0.3 },
            }}
            exit={{
              height: 0,
              opacity: 0,
              scale: 0.9,
              transition: { type: "tween", delay: 0.2 },
            }}
          >
            {!mustSaveCard && !isAlreadyExisting && <Spacer />}
            {displaySavePaymentOptionsInside() ? (
              <SavedPaymentOptionsInside>
                {hasMobilePhone && (
                  <FnrEnrichedCheckbox
                    on={isTextToGiveEnabled}
                    toggleOn={toggleIsTextToGiveEnabled}
                    disabled={submitting || deleting}
                  >
                    <FnrCheckboxIcon className="fas fa-mobile-alt" />
                    <FnrCheckboxText>Use for Text to Give</FnrCheckboxText>
                    <FnrCheckboxSubtext>
                      When you text in a keyword to give, we can use this{" "}
                      {isBank ? "account" : "card"}
                      as the payment method.
                    </FnrCheckboxSubtext>
                  </FnrEnrichedCheckbox>
                )}
                {sharableWith?.length > 0 && (
                  <FnrEnrichedCheckbox
                    on={isSharedWithHousehold}
                    toggleOn={toggleIsSharedWithHousehold}
                    disabled={submitting || deleting}
                  >
                    <FnrCheckboxIcon className="fas fa-user-friends" />
                    <FnrCheckboxText>
                      {" "}
                      Share with {shareWithNames}
                    </FnrCheckboxText>
                    <FnrCheckboxSubtext>
                      Also let {shareWithNames} use this{" "}
                      {isBank ? "account" : "card"} to{" "}
                      {verbiage?.leadText?.toLowerCase() || "make payments to"}{" "}
                      {networkName ? ` ${networkName}` : "on Flocknote"}.
                    </FnrCheckboxSubtext>
                  </FnrEnrichedCheckbox>
                )}
                {shouldShowDefaultCheckbox() && (
                  <FnrEnrichedCheckbox
                    on={isDefaultPaymentMethod}
                    toggleOn={handleDefaultOnToggle}
                    disabled={submitting || deleting}
                  >
                    <FnrCheckboxIcon className="fas fa-star" />
                    <FnrCheckboxText>
                      Make this {isBank ? "account" : "card"} my default payment
                      method
                    </FnrCheckboxText>
                  </FnrEnrichedCheckbox>
                )}

                {!isPaymentForm ? (
                  <>
                    <FnrEnrichedCheckbox
                      on={showNickname}
                      toggleOn={handleNicknameToggle}
                      style={{ paddingBottom: 4 }}
                      disabled={submitting || deleting}
                    >
                      <FnrCheckboxIcon className="fas fa-tag" />
                      <FnrCheckboxText>
                        Give this {isBank ? "account" : "card"} a nickname to
                        help remember it easier
                      </FnrCheckboxText>
                    </FnrEnrichedCheckbox>
                    <AnimatePresence initial={false}>
                      {showNickname && (
                        <NicknameHolder
                          key="nickname"
                          onAnimationComplete={nickNameAnimateInComplete}
                          initial={{ height: 0, opacity: 0, scale: 0.8 }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                            y: 0,
                            scale: 1,
                          }}
                          exit={{
                            height: 0,
                            opacity: 0,
                            scale: 0.8,
                            y: -10,
                            transition: { type: "tween", duration: 0.2 },
                          }}
                        >
                          <NicknameInputPadding>
                            <FnrInput
                              placeholder={
                                isBank ? "Account Nickname" : "Card Nickname"
                              }
                              value={nickname}
                              onChange={setNickname}
                              maxLength="32"
                              name="nickname"
                              autoComplete={false}
                              size="small"
                              ref={nicknameInputRef}
                              disabled={submitting || deleting}
                              autoFocus={
                                !hasOtherPaymentMethods &&
                                !hasMobilePhone &&
                                sharableWith?.length === 0
                              }
                            />
                          </NicknameInputPadding>
                        </NicknameHolder>
                      )}
                    </AnimatePresence>
                  </>
                ) : null}

                {isAlreadyExisting && (
                  <DeleteButtonContainer
                  >
                    <FnrHoverButton
                      ref={deleteButtonRef}
                      size="small"
                      forceHover={showDeleteConfirmation || deleting}
                      disabled={submitting}
                      onClick={() =>
                        submitting || deleting
                          ? null
                          : setShowDeleteConfirmation((prev) => !prev)
                      }
                    >
                      <DeleteButton
                        data-cy="AdditionalCardSettings_DeletePaymentMethodButton"
                        whileHover={{ color: "#F42434" }}
                        animate={{
                          color:
                            deleting || showDeleteConfirmation
                              ? "#F42434"
                              : "#15436a",
                        }}
                      >
                        <DeleteIcon className="far fa-trash-alt" />
                        <div>Delete {isBank ? "Account" : "Card"}</div>
                      </DeleteButton>
                    </FnrHoverButton>
                    <DeleteConfirmationPopover
                      buttonRef={deleteButtonRef}
                      show={showDeleteConfirmation}
                      onShouldClose={closeDeleteConfirmation}
                    />
                  </DeleteButtonContainer>
                )}
              </SavedPaymentOptionsInside>
            ) : null}
            <Spacer />
          </SavedPaymentOptions>
        )}
      </AnimatePresence>
    </Wrapper>
  );
}

const Wrapper = styled.div``;
const SavedPaymentOptions = styled(motion.div)`
  margin-left: -8px;
  margin-right: -8px;
  padding: 0 8px;
  overflow: hidden;
`;
const SavedPaymentOptionsInside = styled.div`
  padding: 8px 12px;
  padding-bottom: 16px;
  border-radius: 8px;
  background: radial-gradient(circle at bottom right, #f6f7f8, #fff);
  box-shadow: rgba(0, 0, 0, 0.2) 0 1px 4px;
`;
const Spacer = styled.div`
  height: 8px;
`;
const NicknameHolder = styled(motion.div)`
  overflow: hidden;
  box-sizing: border-box;
  padding: 0 48px;
`;
const NicknameInputPadding = styled.div`
  padding-top: 8px;
  padding-bottom: 4px;
`;
const DeleteButton = styled(motion.div)`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 400;
`;
const DeleteButtonContainer = styled.div`
  display: flex;
  margin-top: 12px;
  margin-bottom: -6px;
`;
const DeleteIcon = styled.div`
  display: block;
  margin-right: 8px;
`;
